import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import EventsTimeFilter from './EventsTimeFilter';
import GroupsFilterModule from './GroupsFilterModule';

const GroupsFilterBar = ({
  groupKey,
  onRefresh,
  onTimeFilterChange,
  onSearchChange,
  searchTerm = '',
  selectedTimePreset = '30d',
  lastRefreshTime = null,
  showTitle = true,
  showRefresh = true,
  onEditColumns = () => {},
  filters = [],
  onFiltersChange = () => {},
  teamId = null
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const searchInputRef = useRef(null);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleSearch = (value) => {
    const trimmedValue = (value || '').trim();
    setLocalSearchTerm(trimmedValue);
    onSearchChange(trimmedValue);
  };

  const handleSearchClear = () => {
    setLocalSearchTerm('');
    onSearchChange('');
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const formatLastRefresh = () => {
    if (!lastRefreshTime) return '';
    const now = new Date();
    const diffMins = Math.floor((now - lastRefreshTime) / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins === 1) return '1 minute ago';
    return `${diffMins} minutes ago`;
  };

  const groupKeyLabel = groupKey
    ? groupKey.replace(/_id$/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'Groups';

  return (
    <div className="bg-white" style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* Title and Refresh Row */}
      {(showTitle || showRefresh) && (
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showTitle && (
              <h1 className="text-xl font-semibold text-[#0f172a]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                {groupKeyLabel}
              </h1>
            )}
            {showRefresh && (
              <button
                onClick={onRefresh}
                className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0f172a] transition-colors"
                title={`Refresh ${groupKeyLabel}`}
              >
                <RefreshCw className="h-4 w-4" />
                {lastRefreshTime && (
                  <span className="text-xs">Updated {formatLastRefresh()}</span>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filter Bar Row */}
      <div className="px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
        {/* Left: Time Filter (based on updated_at) */}
        <div className="min-w-[520px]">
          <EventsTimeFilter onTimeFilterChange={onTimeFilterChange} selectedPreset={selectedTimePreset} />
        </div>

        {/* Right: actions + search */}
        <div className="flex items-center gap-4 ml-auto">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-3 py-1 text-sm font-medium bg-white text-[#475569] hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2"
          >
            <Icon name="filter" size={18} color="#475569" />
            {showFilters ? 'Hide Filter' : 'Show Filter'}
          </button>

          <button
            onClick={onEditColumns}
            className="px-3 py-1 text-sm font-medium bg-white text-[#475569] hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2"
            title="Edit columns"
          >
            <Icon name="edit_pencil" size={18} color="#475569" />
            Edit Columns
          </button>

          <div className="w-[260px] ml-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#475569]" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={`Search by ${groupKeyLabel.toLowerCase()} ID...`}
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSearch(e.target.value || '');
                  }
                }}
                className="w-full pl-10 pr-10 py-1 text-sm bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d0c46] focus:border-transparent"
              />
              {localSearchTerm && (
                <button
                  type="button"
                  onClick={handleSearchClear}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#475569] hover:text-[#475569]"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters panel */}
      <div className={`px-6 py-4 bg-transparent transition-all duration-200 ${showFilters ? 'block' : 'hidden'}`}>
        <GroupsFilterModule
          filters={filters}
          onFiltersChange={onFiltersChange}
          teamId={teamId}
          groupKey={groupKey}
        />
      </div>
    </div>
  );
};

export default GroupsFilterBar;
